import { useState } from "react";
import { getFirestoreService } from "../../firebase/services";
import { usePersistentState } from "../../hooks/usePersistentState";

export const useContactForm = (initialFormData) => {
  const [formData, setFormData, clearStoredFormData] = usePersistentState(
    "rfa_contact_form_data_v1",
    initialFormData,
  );

  const [status, setStatus] = useState({ type: "", message: "" });
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});

  const sanitizeField = (fieldName, rawValue) => {
    const value = String(rawValue ?? "");

    const maxLengths = {
      name: 100,
      email: 254,
      phone: 25,
      message: 2000,
    };
    const limit = maxLengths[fieldName] || 500;

    if (fieldName === "name") {
      return value
        .slice(0, limit)
        .replace(/\s+/g, " ")
        .replace(/[^a-zA-Z\s.'-]/g, "");
    }

    if (fieldName === "email") {
      return value.slice(0, limit).trim().toLowerCase();
    }

    if (fieldName === "phone") {
      return value
        .slice(0, limit)
        .replace(/\s+/g, " ")
        .replace(/[^0-9+()\-\s]/g, "");
    }

    if (fieldName === "message") {
      return value.slice(0, limit).replace(/\r\n/g, "\n");
    }

    return value.slice(0, limit);
  };

  const validate = (data) => {
    const nextErrors = {};

    const name = data.name.trim();
    if (name.length < 2) nextErrors.name = "Please enter your full name.";

    const email = data.email.trim().toLowerCase();
    const emailOk = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/i.test(email);
    if (!emailOk) nextErrors.email = "Please enter a valid email address.";

    const phone = data.phone.trim();
    const phoneDigits = phone.replace(/\D/g, "");
    if (phoneDigits.length < 7)
      nextErrors.phone = "Please enter a valid phone number.";

    const message = data.message.trim();
    if (message.length < 10)
      nextErrors.message = "Message should be at least 10 characters.";

    return nextErrors;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    const sanitizedValue = sanitizeField(name, value);
    setFormData({ ...formData, [name]: sanitizedValue });
    setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus({ type: "", message: "" });

    const sanitized = {
      name: sanitizeField("name", formData.name).trim(),
      email: sanitizeField("email", formData.email).trim(),
      phone: sanitizeField("phone", formData.phone).trim(),
      message: sanitizeField("message", formData.message).trim(),
    };

    const nextErrors = validate(sanitized);
    if (Object.keys(nextErrors).length > 0) {
      setErrors(nextErrors);
      setStatus({
        type: "error",
        message: "Please fix the highlighted fields and try again.",
      });
      return;
    }

    setLoading(true);

    try {
      const db = await getFirestoreService();
      const { collection, addDoc, serverTimestamp } = await import("firebase/firestore");
      const messagesRef = collection(db, "contactMessages");
      await addDoc(messagesRef, {
        ...sanitized,
        status: "unread",
        submittedAt: serverTimestamp(),
      });

      setStatus({
        type: "success",
        message: "Message sent successfully! We will get back to you soon.",
      });

      clearStoredFormData();
      setFormData(initialFormData);
      setErrors({});

      setTimeout(() => {
        setStatus({ type: "", message: "" });
      }, 5000);
    } catch (error) {
      console.error("Error adding document: ", error);
      setStatus({
        type: "error",
        message: "Something went wrong. Please try again.",
      });
    } finally {
      setLoading(false);
    }
  };

  return {
    formData,
    status,
    loading,
    errors,
    handleChange,
    handleSubmit,
  };
};
